import { ThemeProvider, useTheme } from 'styled-components';
import { useThemeFromType } from '../../hooks/usePreferredTheme';
import { ThemeTypes } from '../../styles/themes/types';
import { Button, ButtonGroup, ButtonWrapper, IconButton } from '../Button';
import { Flex } from '../Core';
import { FormControlSizes } from '../Form';
import { Icon, IconName } from '../Icons';
import { Text } from '../Text';
import type { ITourStepProps } from './types';

export function TourStep({
  title,
  description,
  showClose = false,
  showNavigation = true,
  readMoreURL,
  onDismiss,
  onDismissAll,
  onNext,
  onPrev,
}: ITourStepProps) {
  const showActionButtons = onNext || onPrev || onDismiss || onDismissAll;

  const theme = useTheme();
  const darkTheme = useThemeFromType(theme.themeName, ThemeTypes.dark);

  return (
    <ThemeProvider theme={darkTheme}>
      <Flex flexDirection="column" maxWidth="260px" gap="spacing12" data-testid="tour-step" id="tour-step">
        <Flex alignItems="center" justifyContent="space-between">
          <Flex color="colorTextImportant" fontWeight="fontWeightBold" gap="spacing8" alignItems="center" w="100%">
            {title}
            {readMoreURL && (
              <ButtonWrapper as="a" size={FormControlSizes.Tiny} href={readMoreURL} target="_blank">
                Read more <Icon icon={IconName.ExternalLink} />
              </ButtonWrapper>
            )}
          </Flex>
          {showClose && (
            <IconButton
              icon={IconName.Close}
              size={FormControlSizes.Tiny}
              onClick={e => {
                e.stopPropagation();
                onDismiss?.();
              }}
              ghost={true}
            />
          )}
        </Flex>
        {description && (
          <Text color="colorTextImportant" fontSize="fontSizeSmall">
            {description}
          </Text>
        )}
        {showActionButtons && (
          <Flex justifyContent="space-between">
            <Flex gap="spacing4">
              {onDismiss != null && (
                <Button
                  size={FormControlSizes.Tiny}
                  onClick={e => {
                    onDismiss();
                    e.stopPropagation();
                  }}
                  data-testid="tour-dismiss-button"
                >
                  Dismiss
                </Button>
              )}
              {onDismissAll != null && (
                <Button
                  size={FormControlSizes.Tiny}
                  ghost={true}
                  onClick={e => {
                    onDismissAll();
                    e.stopPropagation();
                  }}
                  data-testid="tour-dismiss-all-button"
                >
                  Stop showing tips
                </Button>
              )}
            </Flex>
            {showNavigation && (
              <ButtonGroup size={FormControlSizes.Tiny}>
                <IconButton
                  icon={IconName.ChevronLeft}
                  size={FormControlSizes.Tiny}
                  onClick={e => {
                    onPrev?.();
                    e.stopPropagation();
                  }}
                  disabled={onPrev == null}
                />
                <IconButton
                  icon={IconName.ChevronRight}
                  size={FormControlSizes.Tiny}
                  onClick={e => {
                    onNext?.();
                    e.stopPropagation();
                  }}
                  disabled={onNext == null}
                  data-testid="tour-next-button"
                />
              </ButtonGroup>
            )}
          </Flex>
        )}
      </Flex>
    </ThemeProvider>
  );
}
